import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import dayjs from 'dayjs'
import {
    Bell, Bookmark, Calendar, CarFront, CircleCheck, CircleDollarSign, Clock, CreditCard,
    Gift, Heart, Hourglass, Info, PartyPopper, ShieldAlert, ShoppingBag, Ticket, TicketCheck, TrendingUp,
    Trophy, Undo2, UserCheck, UserPlus, Users, Wallet, X,
} from 'lucide-react'
import notificationsApi, { AUDIENCE } from 'api/requests/notifications'
import { announceNotificationsRead } from 'hooks/useNotificationBell'
import { resolveNotificationPath } from 'helpers/notificationLink'
import Skeleton from 'components/_UI/Skeleton'
import Loader from 'components/_UI/Loader'
import colors from 'theme/colors'

const BRAND = colors.occasionalPurple

/**
 * Notificações da plataforma, mais recentes primeiro. `audience` escolhe o
 * feed: o do comprador (mesma lista do app: pagamentos, ingressos, eventos,
 * pedidos e social) ou o do painel do produtor (saques, saldo, vendas).
 *
 * Feita para viver dentro do painel do sino: ocupa a altura que o painel der e
 * rola por conta própria. Quem monta só a mostra aberta, então cada abertura
 * busca de novo.
 *
 * Abrir a lista marca tudo como lido na API, mas as que chegaram novas
 * continuam com o ponto enquanto ela está aberta: sumir na hora não daria
 * tempo de ver quais eram. O contador do sino zera na hora.
 *
 * `onNavigate` avisa que um toque levou a outra página (o painel fecha).
 */
export default function NotificationsList({ audience = AUDIENCE.CONSUMER, onNavigate }) {
    const [items, setItems] = useState([])
    const [page, setPage] = useState(1)
    const [lastPage, setLastPage] = useState(1)
    const [loading, setLoading] = useState(true)
    const [loadingMore, setLoadingMore] = useState(false)
    const [failed, setFailed] = useState(false)
    const [moreFailed, setMoreFailed] = useState(false)

    const mounted = useRef(true)
    const scrollerRef = useRef(null)
    const sentinelRef = useRef(null)

    useEffect(() => () => { mounted.current = false }, [])

    const load = useCallback(async (nextPage = 1) => {
        try {
            const { data } = await notificationsApi.list(audience, nextPage)
            if (!mounted.current) return

            setItems(current => {
                if (nextPage === 1) return data.data

                // Aviso novo chegando entre uma página e outra desloca o
                // offset, e o último da página anterior viria de novo.
                const known = new Set(current.map(item => item.id))
                return [...current, ...data.data.filter(item => !known.has(item.id))]
            })
            setPage(data.meta?.current_page ?? nextPage)
            setLastPage(data.meta?.last_page ?? nextPage)
            setFailed(false)
            setMoreFailed(false)

            if (nextPage === 1 && data.meta?.unread > 0) {
                notificationsApi.readAll(audience)
                    .then(() => announceNotificationsRead(audience))
                    .catch(() => {})
            }
        } catch (error) {
            if (!mounted.current) return

            if (nextPage === 1) setFailed(true)
            else setMoreFailed(true)
        } finally {
            if (mounted.current) {
                setLoading(false)
                setLoadingMore(false)
            }
        }
    }, [audience])

    useEffect(() => {
        load(1)
    }, [load])

    const hasMore = page < lastPage

    const loadMore = useCallback(() => {
        setMoreFailed(false)
        setLoadingMore(true)
        load(page + 1)
    }, [load, page])

    function retry() {
        setFailed(false)
        setLoading(true)
        load(1)
    }

    // Rolagem infinita: a próxima página vem quando o fim da lista se aproxima.
    // O observador olha o contêiner de rolagem do painel, não a janela, e é
    // refeito a cada página; se ela não preencher o painel, o fim continua à
    // vista e a seguinte vem sem precisar rolar.
    useEffect(() => {
        const node = sentinelRef.current

        if (!node || loading || loadingMore || moreFailed || !hasMore) return undefined

        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) loadMore()
        }, { root: scrollerRef.current, rootMargin: '160px' })

        observer.observe(node)

        return () => observer.disconnect()
    }, [loading, loadingMore, moreFailed, hasMore, loadMore])

    function renderBody() {
        if (loading) {
            return (
                <List aria-busy='true' aria-label='Carregando notificações'>
                    {[0, 1, 2, 3].map(n => (
                        <li key={n}>
                            <SkeletonRow>
                                <Skeleton width='36px' height='36px' rounded />
                                <SkeletonText>
                                    <Skeleton width='88%' height='14px' />
                                    <Skeleton width='28%' height='12px' />
                                </SkeletonText>
                            </SkeletonRow>
                        </li>
                    ))}
                </List>
            )
        }

        if (failed) {
            return (
                <State role='alert'>
                    <Info size={28} color={colors.nightLight} aria-hidden='true' />
                    <StateTitle>Não foi possível carregar</StateTitle>
                    <StateText>Verifique sua conexão e tente de novo.</StateText>
                    <RetryButton type='button' onClick={retry}>Tentar de novo</RetryButton>
                </State>
            )
        }

        if (!items.length) {
            return (
                <State>
                    <Bell size={28} color={colors.nightLight} aria-hidden='true' />
                    <StateTitle>Nenhuma notificação</StateTitle>
                    <StateText>{EMPTY_TEXT[audience] || EMPTY_TEXT[AUDIENCE.CONSUMER]}</StateText>
                </State>
            )
        }

        return (
            <>
                <List>
                    {items.map(item => (
                        <li key={item.id}>
                            <NotificationRow item={item} onNavigate={onNavigate} />
                        </li>
                    ))}
                </List>

                {hasMore && !moreFailed && <div ref={sentinelRef} aria-hidden='true' />}
                {loadingMore && <Loader size='small' />}
                {moreFailed && (
                    <MoreFailed>
                        <RetryButton type='button' onClick={loadMore}>Tentar de novo</RetryButton>
                    </MoreFailed>
                )}
            </>
        )
    }

    return (
        <Scroller ref={scrollerRef}>
            {renderBody()}
        </Scroller>
    )
}

function NotificationRow({ item, onNavigate }) {
    const unread = !item.read_at
    const { Icon, tone } = TYPE_STYLE[item.type] || TYPE_STYLE.generic
    const path = resolveNotificationPath(item.data?.url)

    const content = (
        <>
            <IconCircle $tone={tone}>
                <Icon size={18} aria-hidden='true' />
            </IconCircle>

            <Body>
                <Message $unread={unread}>{capitalizeFirst(item.text)}</Message>
                <Time dateTime={item.created_at} title={dayjs(item.created_at).format('DD/MM/YYYY [às] HH:mm')}>
                    {timeAgo(item.created_at)}
                </Time>
            </Body>

            {unread && <UnreadDot role='img' aria-label='Não lida' />}
        </>
    )

    // Sem destino (link ausente ou que o site não tem) a notificação só informa.
    if (!path) return <Row $unread={unread}>{content}</Row>

    return <Row as={Link} to={path} onClick={onNavigate} $unread={unread} $clickable>{content}</Row>
}

const EMPTY_TEXT = {
    [AUDIENCE.CONSUMER]: 'Avisos sobre pagamentos, ingressos, eventos e amigos aparecem aqui.',
    [AUDIENCE.PRODUCER]: 'Avisos sobre vendas, lotes, saldo, saques e reembolsos dos seus eventos aparecem aqui.',
}

/**
 * Ícone e tom por `type` (App\Domain\NotificationType na API). Tipo que esta
 * versão não conhece cai no sino — a API pode ganhar tipos antes do site.
 */
const TYPE_STYLE = {
    generic: { Icon: Bell, tone: 'brand' },
    payment_approved: { Icon: CircleCheck, tone: 'success' },
    payment_refused: { Icon: CreditCard, tone: 'danger' },
    order_expired: { Icon: Clock, tone: 'warning' },
    order_refunded: { Icon: CreditCard, tone: 'brand' },
    ticket_received: { Icon: Gift, tone: 'brand' },
    event_today: { Icon: Calendar, tone: 'brand' },
    event_tomorrow: { Icon: Calendar, tone: 'brand' },
    event_changed: { Icon: Calendar, tone: 'warning' },
    event_canceled: { Icon: X, tone: 'danger' },
    certificate_ready: { Icon: Trophy, tone: 'success' },
    giveaway_won: { Icon: Gift, tone: 'success' },
    favorite_soon: { Icon: Bookmark, tone: 'brand' },
    new_follower: { Icon: UserPlus, tone: 'brand' },
    new_friend: { Icon: Users, tone: 'brand' },
    event_match: { Icon: Heart, tone: 'brand' },
    order_ready: { Icon: ShoppingBag, tone: 'success' },
    order_out_for_delivery: { Icon: CarFront, tone: 'success' },
    event_invite: { Icon: Ticket, tone: 'brand' },
    event_invite_accepted: { Icon: UserCheck, tone: 'brand' },
    friend_request: { Icon: UserPlus, tone: 'brand' },
    friend_request_accepted: { Icon: Users, tone: 'brand' },

    // Painel do produtor
    withdraw_approved: { Icon: Wallet, tone: 'success' },
    withdraw_rejected: { Icon: Wallet, tone: 'danger' },
    balance_released: { Icon: CircleDollarSign, tone: 'success' },
    sale_refunded: { Icon: Undo2, tone: 'warning' },
    sale_disputed: { Icon: ShieldAlert, tone: 'warning' },
    sale_chargeback: { Icon: ShieldAlert, tone: 'danger' },
    new_sale: { Icon: Ticket, tone: 'success' },
    tickets_low: { Icon: Hourglass, tone: 'warning' },
    first_sale: { Icon: PartyPopper, tone: 'success' },
    sales_milestone: { Icon: TrendingUp, tone: 'brand' },
    batch_sold_out: { Icon: TicketCheck, tone: 'success' },
    event_sold_out: { Icon: PartyPopper, tone: 'success' },

    // Equipes: o convidado que aceitou e quem o convidou
    team_invite_accepted: { Icon: Users, tone: 'success' },
    team_member_joined: { Icon: UserCheck, tone: 'success' },
}

const TONE_INK = {
    brand: BRAND,
    success: colors.successDark,
    warning: colors.goldDark,
    danger: colors.danger,
}

// Notificações antigas começam em minúscula ("o usuário ...").
function capitalizeFirst(text = '') {
    return text.charAt(0).toUpperCase() + text.slice(1)
}

function timeAgo(value) {
    const date = dayjs(value)
    const minutes = dayjs().diff(date, 'minute')

    if (minutes < 1) return 'Agora'
    if (minutes < 60) return `Há ${minutes} min`

    const hours = dayjs().diff(date, 'hour')
    if (hours < 24) return `Há ${hours} h`

    const days = dayjs().startOf('day').diff(date.startOf('day'), 'day')
    if (days === 1) return 'Ontem'
    if (days < 7) return `Há ${days} dias`

    return date.format('DD/MM/YYYY')
}

const DIVIDER = 'rgba(112, 112, 112, 0.12)'

// Ocupa o que sobra do painel e rola por dentro; `overscroll-behavior` evita
// que o fim da lista passe a rolar a página por trás.
const Scroller = styled.div`
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
`

const List = styled.ul`
    margin: 0;
    padding: 0;
    list-style: none;

    & > li + li {
        border-top: 1px solid ${DIVIDER};
    }
`

const Row = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 12px 16px;
    background: ${({ $unread }) => ($unread ? `${BRAND}0D` : 'transparent')};
    color: inherit;
    text-decoration: none;

    ${({ $clickable, $unread }) => $clickable && `
        cursor: pointer;
        transition: background 0.15s ease;

        &:hover {
            background: ${$unread ? `${BRAND}17` : 'rgba(112, 112, 112, 0.06)'};
        }

        &:focus-visible {
            outline: 2px solid ${BRAND};
            outline-offset: -2px;
        }
    `}
`

const IconCircle = styled.span`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    color: ${({ $tone }) => TONE_INK[$tone]};
    background: ${({ $tone }) => `${TONE_INK[$tone]}1F`};
`

const Body = styled.div`
    flex: 1;
    min-width: 0;
`

const Message = styled.p`
    margin: 0;
    font-size: 14px;
    line-height: 1.4;
    font-weight: ${({ $unread }) => ($unread ? 700 : 400)};
    color: ${colors.nightDark};
    overflow-wrap: anywhere;
`

const Time = styled.time`
    display: block;
    margin-top: 4px;
    font-size: 12px;
    color: ${colors.night};
`

const UnreadDot = styled.span`
    flex-shrink: 0;
    width: 8px;
    height: 8px;
    margin-top: 6px;
    border-radius: 50%;
    background: ${BRAND};
`

const SkeletonRow = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 12px 16px;
`

const SkeletonText = styled.div`
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 8px;
    padding-top: 2px;
`

const State = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 40px 24px;
    text-align: center;
`

const StateTitle = styled.p`
    margin: 6px 0 0;
    font-size: 15px;
    font-weight: 600;
    color: ${colors.nightDark};
`

const StateText = styled.p`
    margin: 0;
    max-width: 280px;
    font-size: 13px;
    line-height: 1.5;
    color: ${colors.night};
`

const RetryButton = styled.button`
    margin-top: 10px;
    padding: 8px 16px;
    border-radius: 10px;
    border: 1.5px solid ${BRAND}55;
    background: transparent;
    color: ${BRAND};
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s ease;

    &:hover {
        background: ${BRAND}14;
    }
`

const MoreFailed = styled.div`
    display: flex;
    justify-content: center;
    padding: 4px 0 12px;
`
